<script lang="ts">
    import type {Form} from 'src/typings';
    import {getLocalization, getLogger} from 'src/core';
    import type {SearchFiltersEventsMap} from './events';
    import {createEventDispatcher} from 'svelte';
    import {isNullOrUndefined, uuid as generateUuid} from 'shared/utils/custom-utils';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import {omitPropertiesBy} from 'shared/utils/object-utils';
    import {isEqual} from 'lodash-es';
    import KpSearchAdditionalFilterField from './KpSearchAdditionalFilterField.svelte';
    import KpGenericPanel from 'shared/ui-widgets/panel/KpGenericPanel.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';

    export let filters: Form[];
    export let params: Record<string, any>;

    const logger = getLogger();
    const dispatch = createEventDispatcher<SearchFiltersEventsMap>();
    const localize = getLocalization();

    initializeEmptyParamProperties();

    let lastSubmittedParams: Record<string, any>;

    let isComponentVisible: boolean;
    $: isComponentVisible = !isNullOrUndefined(filters) && Array.isArray(filters) && filters.length > 0;

    $: if (!haveUniqueFormIds(filters)) {
        filters = filters.map((filter) => ({...filter, id: generateUuid()}));
        logger.warn('kp-search-additional-filters: forms do not have unique ids - generating random ids as fallback');
    }

    function submitForm() {
        const trimmedParams = omitPropertiesBy(params, (value) => value === null);
        if (!isEqual(trimmedParams, lastSubmittedParams)) {
            dispatch('submit-search');
            lastSubmittedParams = trimmedParams;
        }
    }

    function updateParams() {
        dispatch('params-change', params);
    }

    function haveUniqueFormIds(forms: Form[]) {
        forms = isNullOrUndefined(forms) ? [] : forms;
        const allIds = forms.map((form) => form.id);
        const uniqueIds = [...new Set(allIds)];

        return allIds.length === uniqueIds.length;
    }

    function initializeEmptyParamProperties() {
        // add properties missing in params, otherwise svelte will warn about KpValueEditor created with missing model prop
        filters.flatMap((filter) => filter.fields).forEach((field) => {
            if (!Object.hasOwn(params, field.fieldName)) {
                params[field.fieldName] = null;
            }
        });
    }
</script>

{#if isComponentVisible}
    <form class="search-additional-filter-container"
          name="search-filters"
          aria-label={localize(/* @kp-localization search.accessibility.searchResultFilters */ 'search.accessibility.searchResultFilters')}
          on:submit|preventDefault={submitForm}>

        <KpGenericPanel hasBodyPadding={false}>
            <div class="facets-panel-body">
                {#each filters as filter (filter.id)}
                    <div class="facet" role="group" aria-labelledby="{filter.id}-label">
                        <div id="{filter.id}-label" class="facet-name">
                            {pipe(filter, loc())}
                        </div>

                        {#each filter.fields as field (field.editor.editorId ?? field.fieldName)}
                            <KpSearchAdditionalFilterField {field}
                                                           bind:model={params[field.fieldName]}
                                                           isSingleField={filter.fields.length <= 1}
                                                           on:model-change={updateParams}/>
                        {/each}
                    </div>
                {/each}

                <div class="search-button">
                    <KpButton isBlock type="submit" buttonStyle="primary">
                        <IconedContent icon="search">
                            {localize(/* @kp-localization commons.hledat */ 'commons.hledat')}
                        </IconedContent>
                    </KpButton>
                </div>
            </div>
        </KpGenericPanel>
    </form>
{/if}

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .facets-panel-body {
        .facet-name {
            border-top: 0;
            padding-top: @spacing-sm;
        }

        .search-button {
            position: sticky;
            bottom: 0;
            z-index: 2; // to ensure it will be at least at the same level as inputs and form controls
            padding: @spacing-m @spacing-sm @spacing-sm;

            .facet-name {
                padding: @spacing-sm @spacing-sm 0 @spacing-sm;
            }
        }
    }
</style>